export interface QualificationEntry {
  id: string
  school_id: string
  background_id: string
  background_name: string | null
  background_code: string | null
  subject_id: string
  subject_name: string | null
  subject_code: string | null
  qualification_type: 'qualified' | 'group'
  group_name: string | null
  priority: number
  requires_same_class_year: boolean
  class_year: string | null
  min_years_experience: number | null
  notes: string | null
  is_verified: boolean
  verified_by_id: string | null
  verified_at: string | null
  import_source: string | null
  import_batch_id: string | null
  created_at: string
  updated_at: string
}

export interface SubjectQualificationGroupMember {
  id: string
  school_id: string
  group_id: string
  background_id: string
  background_name: string | null
  background_code: string | null
  created_at: string
  updated_at: string
}

export interface SubjectQualificationGroup {
  id: string
  school_id: string
  subject_id: string
  subject_name: string | null
  subject_code: string | null
  name: string
  description: string | null
  match_mode: 'or' | 'and'
  is_active: boolean
  members: SubjectQualificationGroupMember[]
  created_at: string
  updated_at: string
}

export interface SubjectQualificationGroupListResponse {
  groups: SubjectQualificationGroup[]
  total: number
}

export interface Background {
  id: string
  school_id: string
  name: string
  code: string | null
  description: string | null
  is_active: boolean
  created_at: string
  teacher_count?: number
}

export interface Subject {
  id: string
  school_id: string
  name: string
  code: string | null
  description: string | null
  class_year: string | null
  is_active: boolean
  qualification_override?: 'cells' | 'all' | 'none'
  substitution_excluded?: boolean
  created_at: string
}

export interface BackgroundImportInfo {
  name: string
  exists: boolean
  existing_id: string | null
}

export interface SubjectImportInfo {
  name: string
  exists: boolean
  existing_id: string | null
}

export interface QualificationEntryImportData {
  background_name: string
  subject_name: string
  qualification_type: 'qualified' | 'group'
  group_name: string | null
  priority: number
}

export interface ImportPreview {
  backgrounds_to_create: number
  backgrounds_existing: number
  subjects_to_create: number
  subjects_existing: number
  entries_count: number
  backgrounds: BackgroundImportInfo[]
  subjects: SubjectImportInfo[]
  entries: QualificationEntryImportData[]
  warnings: string[]
  confidence: number
}

export interface ImportResult {
  success: boolean
  batch_id: string
  entries_created: number
  entries_updated: number
  backgrounds_created: number
  subjects_created: number
  groups_created: number
  groups_updated: number
  warnings: string[]
  type_counts: Record<string, number>
}

export interface EligibleTeacherInfo {
  id: string
  code: string | null
  display_name: string
  qualification_type: 'qualified' | 'group'
  group_name: string | null
  priority: number
  class_year_match: boolean
}

export interface EligibleTeachersResponse {
  subject_id: string
  subject_name: string
  teachers: EligibleTeacherInfo[]
}

export interface QualificationLookupResponse {
  background_id: string
  background_name: string
  qualified_subjects: QualificationEntry[]
}

export interface BatchDetail {
  batch_id: string
  import_source: string
  entries_created: number
  entries_updated: number
  backgrounds_created: number
  subjects_created: number
  imported_at: string
  warnings: string[]
}

export interface MatrixScanCell {
  row: number
  col: number
  col_letter: string
  value: string | null
}

export interface MatrixScanResponse {
  success: boolean
  file_name: string
  max_row: number
  max_col: number
  cells: MatrixScanCell[]
  suggested_subject_row: number | null
  suggested_background_col: number | null
  suggested_data_start_row: number | null
  suggested_data_start_col: number | null
  warnings: string[]
}

export interface MatrixMapping {
  subject_header_row: number
  background_header_col: number
  data_start_row: number
  data_start_col: number
}

// ─── v7: Qualification Management ────────────────────────────────────────────

export interface QualificationReport {
  teachers_total: number
  teachers_with_backgrounds: number
  teachers_with_subjects: number
  teachers_with_qualifications: number
  teachers_missing_subjects: { teacher_id: string; name: string; backgrounds: string[] }[]
  teachers_missing_subjects_count: number
  subjects_without_teachers: { subject_id: string; name: string }[]
  qualification_coverage: number
  cache_entries_total: number
  last_cache_rebuild: string | null
}

export interface CacheRebuildResponse {
  success: boolean
  qualifications_created: number
  message: string
}

// ─── Color Helpers ───────────────────────────────────────────────────────────

export const QUALIFICATION_TYPE_LABELS: Record<string, string> = {
  qualified: 'Qualifiziert',
  group: 'Gruppe',
}

export const QUALIFICATION_TYPE_COLORS: Record<'qualified' | 'group', string> = {
  qualified: 'bg-green-100 text-green-800',
  group: 'bg-blue-100 text-blue-800',
}

export function getQualificationColor(qualificationType: string, groupName: string | null): string {
  if (qualificationType === 'qualified') {
    return QUALIFICATION_TYPE_COLORS.qualified
  }
  if (groupName) {
    const hash = groupName.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800',
      'bg-purple-100 text-purple-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800',
      'bg-red-100 text-red-800',
      'bg-teal-100 text-teal-800',
      'bg-orange-100 text-orange-800',
      'bg-cyan-100 text-cyan-800',
    ]
    return colors[hash % colors.length] ?? 'bg-gray-100 text-gray-800'
  }
  return QUALIFICATION_TYPE_COLORS.group
}

export function getGroupColor(groupName: string | null): string {
  return getQualificationColor('group', groupName)
}

export function groupChipLabel(
  groupName: string,
  matchMode: 'or' | 'and' | undefined,
  members: { background_id: string; background_name: string | null; background_code: string | null }[],
  currentBackgroundId: string,
): string {
  const numbered = groupName.match(/Gruppe\s+(\d+)\s*$/i)
  const short = numbered ? `G${numbered[1]}` : groupName
  const partners = members
    .filter(m => m.background_id !== currentBackgroundId)
    .map(m => m.background_code || m.background_name)
    .filter((name): name is string => Boolean(name))
  if (matchMode !== 'and' || partners.length === 0) return short
  return `${short} ∧ ${partners.join(', ')}`
}

export function groupChoicesForSubject(subjectName: string, usedGroupNames: string[]): string[] {
  let max = 0
  const re = /Gruppe\s+(\d+)\s*$/i
  for (const name of usedGroupNames) {
    const m = name.match(re)
    if (m) max = Math.max(max, Number(m[1]))
  }
  return Array.from({ length: max + 1 }, (_, i) => `${subjectName} Gruppe ${i + 1}`)
}

export function occupiedGroupNamesForSubject(
  groups: { name: string; members: { background_id: string }[] }[],
  extraNames: string[] = [],
  droppingBackgroundId?: string,
): string[] {
  const names = new Set(extraNames)
  for (const g of groups) {
    if (extraNames.includes(g.name)) continue
    const remaining = droppingBackgroundId
      ? g.members.filter(m => m.background_id !== droppingBackgroundId)
      : g.members
    if (remaining.length > 0) names.add(g.name)
  }
  return [...names]
}

export function nextGroupNameForSubject(subjectName: string, usedGroupNames: string[]): string {
  const used = new Set<number>()
  const re = /Gruppe\s+(\d+)\s*$/i
  for (const name of usedGroupNames) {
    const m = name.match(re)
    if (m) used.add(Number(m[1]))
  }
  let n = 1
  while (used.has(n)) n += 1
  return `${subjectName} Gruppe ${n}`
}
